/**
 * DocRecognizer — preprocess + classify in one call.
 *
 * Runs the brdocs pre-processing pipeline (localize → bgremove → deskew →
 * orient) on the input image, then classifies the rectified result. This is the
 * SAME chain the trainer applied, so inference matches training (the parity the
 * app's CnnClassifier relies on), now packaged as one framework-agnostic step.
 */
import { ImagePixels } from './core/ports';
import { DocPipeline, StageTrace } from './preprocess/pipeline';
import { OverrideContext } from './preprocess/overrides';
import { pixelsToDataUrl } from './preprocess/adapters/canvas-image';
import { DocClassifier, StageImage } from './classify/doc-classifier';
import { ClassifyDebug, DocPrediction } from './classify/types';

export interface RecognizeResult {
  prediction: DocPrediction;
  /** The pipeline output image (rectified, upright before classify rotation). */
  processed: ImagePixels;
  /** Per-stage method trace from the pipeline. */
  stages: StageTrace[];
  /** Rotation (deg) the classifier applied on top to read the document best. */
  fedRotation: number;
  /** Inspection data from the classify pass (only when `debug` is set). */
  debug?: ClassifyDebug;
}

export interface RecognizeOptions {
  /** Per-document pipeline overrides (skip / rotate / exclude). */
  overrides?: OverrideContext;
  /** When true, capture per-stage images + classify debug data. */
  debug?: boolean;
}

export class DocRecognizer {
  private readonly pipeline: DocPipeline;
  private readonly classifier: DocClassifier;

  constructor(o: { pipeline: DocPipeline; classifier: DocClassifier }) {
    this.pipeline = o.pipeline;
    this.classifier = o.classifier;
  }

  async recognize(image: ImagePixels, opts: RecognizeOptions = {}): Promise<RecognizeResult> {
    const debug = !!opts.debug;
    const res = await this.pipeline.process(image, opts.overrides ?? {}, debug);

    // When debugging, surface each pre-processing stage's output image so the
    // classify debug carries the full localize→bgremove→deskew→orient trace.
    const stageImages: StageImage[] = debug
      ? res.stages.map((s) => ({
          name: s.name,
          method: s.method,
          img: s.image ? pixelsToDataUrl(s.image, 'image/png') : '',
        }))
      : [];

    const { prediction, debug: classifyDebug } = await this.classifier.classify(res.image, stageImages);

    return {
      prediction,
      processed: res.image,
      stages: res.stages,
      fedRotation: classifyDebug.chosenDeg,
      debug: debug ? classifyDebug : undefined,
    };
  }
}
